import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Users, Receipt, Settings as SettingsIcon,
  Wrench, LogOut, ChevronRight, ClipboardList, Package, Sparkles, CreditCard, Hammer, Send, Menu,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader,
} from "@/components/ui/sheet";
import { NotificationBell } from "@/components/NotificationBell";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/jobs", label: "Job Board", icon: Hammer, id: "nav-jobs" },
  { to: "/my-bids", label: "My Bids", icon: Send, id: "nav-mybids" },
  { to: "/invoices", label: "Invoices & Estimates", icon: FileText, id: "nav-invoices" },
  { to: "/customers", label: "Customers & Vehicles", icon: Users, id: "nav-customers" },
  { to: "/reports", label: "Health Reports", icon: ClipboardList, id: "nav-reports" },
  { to: "/catalog", label: "Parts & Catalog", icon: Package, id: "nav-catalog" },
  { to: "/ai", label: "AI Assistant", icon: Sparkles, id: "nav-ai" },
  { to: "/expenses", label: "Expenses", icon: Receipt, id: "nav-expenses" },
  { to: "/billing", label: "Billing & Plan", icon: CreditCard, id: "nav-billing" },
  { to: "/settings", label: "Shop Settings", icon: SettingsIcon, id: "nav-settings" },
];

export default function Layout({ children, title, action }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 flex">
      {/* Sidebar */}
      <aside className="hidden md:flex w-72 shrink-0 flex-col border-r border-slate-800/60 bg-slate-950 fixed inset-y-0 left-0 z-30">
        <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-800/60">
          <img src="/pitcrew-logo-square.png" alt="Pitcrew" className="h-9 w-9 rounded-lg object-cover" />
          <div className="leading-tight">
            <p className="font-head font-bold text-slate-50 text-sm">Pitcrew</p>
            <p className="text-[11px] text-slate-500 tracking-wide">SHOP DESK</p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1">
          {NAV.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.id}
                className={({ isActive }) =>
                  `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 border-l-2 ${
                    isActive
                      ? "bg-slate-800/70 text-amber-400 border-amber-500"
                      : "text-slate-400 border-transparent hover:bg-slate-900 hover:text-slate-100"
                  }`
                }
              >
                <Icon className="h-[18px] w-[18px] shrink-0" />
                <span className="truncate">{item.label}</span>
                <ChevronRight className="h-4 w-4 ml-auto opacity-0 -translate-x-1 group-hover:opacity-40 group-hover:translate-x-0 transition-all duration-200" />
              </NavLink>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-800/60">
          <div className="flex items-center gap-3 px-3 py-2 rounded-md bg-slate-900/60">
            <div className="h-9 w-9 rounded-full bg-slate-800 flex items-center justify-center text-amber-400 font-bold text-sm">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-slate-100 truncate">{user?.business_name || user?.name}</p>
              <p className="text-[11px] text-slate-500 truncate">{user?.email}</p>
            </div>
            <Button
              variant="ghost" size="icon"
              data-testid="logout-button"
              onClick={logout}
              className="h-8 w-8 text-slate-500 hover:text-rose-400 hover:bg-slate-800"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-72 flex flex-col min-w-0">
        <header className="sticky top-0 z-20 h-16 flex items-center gap-4 px-5 md:px-8 border-b border-slate-800/60 bg-slate-950/80 backdrop-blur-xl">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                data-testid="mobile-menu-trigger"
                className="md:hidden h-9 w-9 rounded-lg flex items-center justify-center text-slate-300 hover:bg-slate-800 transition-colors"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 bg-slate-950 border-slate-800/60 text-slate-200" data-testid="mobile-menu-sheet">
              <SheetHeader className="flex flex-row items-center gap-3 px-6 h-16 border-b border-slate-800/60 space-y-0">
                <img src="/pitcrew-logo-square.png" alt="Pitcrew" className="h-9 w-9 rounded-lg object-cover" />
                <div className="leading-tight text-left">
                  <SheetTitle className="font-head font-bold text-slate-50 text-sm">Pitcrew</SheetTitle>
                  <p className="text-[11px] text-slate-500 tracking-wide">SHOP DESK</p>
                </div>
              </SheetHeader>
              <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
                {NAV.map((item) => {
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      data-testid={`mobile-${item.id}`}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        `group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors duration-200 border-l-2 ${
                          isActive
                            ? "bg-slate-800/70 text-amber-400 border-amber-500"
                            : "text-slate-400 border-transparent hover:bg-slate-900 hover:text-slate-100"
                        }`
                      }
                    >
                      <Icon className="h-[18px] w-[18px] shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </NavLink>
                  );
                })}
              </nav>
              <div className="p-3 border-t border-slate-800/60">
                <div className="flex items-center gap-3 px-3 py-2 rounded-md bg-slate-900/60">
                  <div className="h-9 w-9 rounded-full bg-slate-800 flex items-center justify-center text-amber-400 font-bold text-sm">
                    {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-slate-100 truncate">{user?.business_name || user?.name}</p>
                    <p className="text-[11px] text-slate-500 truncate">{user?.email}</p>
                  </div>
                  <Button
                    variant="ghost" size="icon"
                    data-testid="mobile-logout-button"
                    onClick={() => { setMobileOpen(false); logout(); }}
                    className="h-8 w-8 text-slate-500 hover:text-rose-400 hover:bg-slate-800"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <img src="/pitcrew-logo-square.png" alt="Pitcrew" data-testid="mobile-logo"
            className="md:hidden h-8 w-8 rounded-lg object-cover cursor-pointer" onClick={() => navigate("/dashboard")} />
          <h1 className="font-head font-bold text-lg md:text-xl text-slate-50 truncate" data-testid="page-title">
            {title}
          </h1>
          <div className="ml-auto flex items-center gap-2 md:gap-3">
            {action}
            <NotificationBell />
          </div>
        </header>
        <main className="flex-1 p-5 md:p-8">{children}</main>
      </div>
    </div>
  );
}
